/**
 * MÉDICOS REMITENTES PAGE - Referring Doctors Management with useServerDataTable hook
 * Gestión de médicos remitentes del laboratorio
 *
 * MIGRADO A useServerDataTable para:
 * - Columnas ordenables (sorting)
 * - Selector de filas por página
 * - Toggle visibilidad de columnas
 * - Persistencia en localStorage
 * - Sincronización con URL
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Stethoscope,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
  Phone,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useMedicoRemitente,
  useActivateMedicoRemitente,
  useDeactivateMedicoRemitente,
  useDeleteMedicoRemitente,
  type MedicoRemitenteListItem,
} from "@/hooks/use-medicos-remitentes";
import { getMedicosRemitentes, getEspecialidadLabel } from "@/hooks/use-medicos-remitentes";

import { MedicoFormDialog } from "./components/medico-form-dialog";

export default function MedicosRemitentesPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedMedicoId, setSelectedMedicoId] = useState<number | null>(null);
  const [selectedMedicoForAction, setSelectedMedicoForAction] =
    useState<MedicoRemitenteListItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Fetch full medico data for editing
  const { data: selectedMedico } = useMedicoRemitente(selectedMedicoId || 0, {
    enabled: !!selectedMedicoId,
  });

  // Only show form dialog when data is ready (or when creating new)
  const isFormReady = selectedMedicoId === null || !!selectedMedico;

  // Mutations
  const activateMutation = useActivateMedicoRemitente({
    onSuccess: () => {
      toast.success(
        t("medicos.toast.activated", { name: selectedMedicoForAction?.nombre_completo })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivateMedicoRemitente({
    onSuccess: () => {
      toast.warning(
        t("medicos.toast.deactivated", { name: selectedMedicoForAction?.nombre_completo })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteMedicoRemitente({
    onSuccess: () => {
      toast.error(t("medicos.toast.deleted", { name: selectedMedicoForAction?.nombre_completo }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((medico: MedicoRemitenteListItem) => {
    setSelectedMedicoId(medico.id);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((medico: MedicoRemitenteListItem) => {
    setSelectedMedicoForAction(medico);
    setConfirmAction(medico.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((medico: MedicoRemitenteListItem) => {
    setSelectedMedicoForAction(medico);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedMedicoId(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedMedicoForAction || !confirmAction) return;

    if (confirmAction === "activate") {
      activateMutation.mutate(selectedMedicoForAction.id);
    } else if (confirmAction === "deactivate") {
      deactivateMutation.mutate(selectedMedicoForAction.id);
    } else if (confirmAction === "delete") {
      deleteMutation.mutate(selectedMedicoForAction.id);
    }

    setIsConfirmOpen(false);
    setSelectedMedicoForAction(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("medicos.toast.created") : t("medicos.toast.updated"));
    setSelectedMedicoId(null);
    refetch();
  };

  // Column definitions with sortable headers
  const columns: ColumnDef<MedicoRemitenteListItem>[] = useMemo(
    () => [
      {
        accessorKey: "primer_apellido", // Campo real de BD para sorting
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("medicos.columns.name")} />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.original.nombre_completo}</span>
            {row.original.es_tratante_gen && (
              <Badge variant="outline" className="text-xs">
                {t("medicos.generic")}
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: "especialidad",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("medicos.columns.specialty")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm">{getEspecialidadLabel(row.original.especialidad) || "-"}</span>
        ),
      },
      {
        accessorKey: "celular",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("medicos.columns.cellphone")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.celular || "-"}</span>,
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("medicos.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const medico = row.original;
          // No permitir editar/eliminar el médico tratante genérico
          if (medico.es_tratante_gen) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(medico)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(medico)}>
                  {medico.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!medico.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(medico)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Server-side data table hook
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getMedicosRemitentes,
    queryKey: "medicos-remitentes",
    storageKey: "medicos_page_size",
    defaultPageSize: 20,
    filters: [{ key: "is_active", defaultValue: "all", urlParam: "status" }],
  });

  // Column labels for view options
  const columnLabels = {
    primer_apellido: t("medicos.columns.name"),
    especialidad: t("medicos.columns.specialty"),
    celular: t("medicos.columns.cellphone"),
    is_active: t("medicos.columns.status"),
  };

  // Mobile card render function
  const renderMobileCard = useCallback(
    (medico: MedicoRemitenteListItem) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{medico.nombre_completo}</span>
            {medico.es_tratante_gen && (
              <Badge variant="outline" className="text-xs">
                {t("medicos.generic")}
              </Badge>
            )}
            <Badge variant={medico.is_active ? "default" : "secondary"} className="shrink-0">
              {medico.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
          {medico.especialidad && (
            <p className="text-sm text-muted-foreground mt-1">
              {getEspecialidadLabel(medico.especialidad)}
            </p>
          )}
          {medico.celular && (
            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span>{medico.celular}</span>
            </div>
          )}
          {!medico.es_tratante_gen && (
            <div className="flex gap-1 mt-3">
              <Button variant="outline" size="sm" onClick={() => handleEdit(medico)}>
                <Pencil className="h-3 w-3 mr-1" />
                {t("common.edit")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleToggleStatus(medico)}>
                    {medico.is_active ? (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        {t("common.deactivate")}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {t("common.activate")}
                      </>
                    )}
                  </DropdownMenuItem>
                  {!medico.is_active && (
                    <DropdownMenuItem
                      onClick={() => handleDelete(medico)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    ),
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Stethoscope className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("medicos.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("medicos.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("medicos.newDoctor")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("medicos.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex gap-2">
              <Select
                value={filters.is_active}
                onValueChange={(value) => setFilter("is_active", value)}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("medicos.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("medicos.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("medicos.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("medicos.noMedicos")}
            hideOnTablet={["celular"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <MedicoFormDialog
        open={isFormOpen && isFormReady}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setSelectedMedicoId(null);
          }
        }}
        medico={selectedMedico || null}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("medicos.confirmActivate")
            : confirmAction === "delete"
              ? t("medicos.confirmDelete")
              : t("medicos.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("medicos.confirmActivateDesc", {
                name: selectedMedicoForAction?.nombre_completo,
              })
            : confirmAction === "delete"
              ? t("medicos.confirmDeleteDesc", {
                  name: selectedMedicoForAction?.nombre_completo,
                })
              : t("medicos.confirmDeactivateDesc", {
                  name: selectedMedicoForAction?.nombre_completo,
                })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
